import { Component } from "@mzw/component";
import { morph } from "@mzw/morph";
import { productCardLoader } from "@mzw/product-card-loader";
import { startViewTransition } from "@mzw/utilities";

export class ProductSwatches extends Component {
	static tagName = "product-swatches";
	static {
		customElements.define(ProductSwatches.tagName, ProductSwatches);
	}

	#titleRestoreTimeout = 0;
	#swatchesObserver;

	connectedCallback() {
		super.connectedCallback();

		if (this.refs.previousButton && this.refs.nextButton) {
			this.#swatchesObserver = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.target === this.refs.swatch.at(0)) {
							if (entry.isIntersecting) {
								this.refs.previousButton.setAttribute("aria-disabled", "true");
							} else {
								this.refs.previousButton.removeAttribute("aria-disabled");
							}
						} else if (entry.target === this.refs.swatch.at(-1)) {
							if (entry.isIntersecting) {
								this.refs.nextButton.setAttribute("aria-disabled", "true");
							} else {
								this.refs.nextButton.removeAttribute("aria-disabled");
							}
						}
					}
				},
				{ threshold: 0.5 }
			);

			this.#swatchesObserver.observe(this.refs.swatch.at(0));
			this.#swatchesObserver.observe(this.refs.swatch.at(-1));
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#swatchesObserver?.disconnect();
	}

	previous() {
		if (this.refs.swatchList.scrollLeft < 10) {
			this.refs.swatchList.scrollTo({
				left: this.refs.swatchList.scrollWidth,
				behavior: "smooth",
			});
		} else {
			this.refs.swatchList.scrollBy({
				left: -this.refs.swatchList.clientWidth,
				behavior: "smooth",
			});
		}
	}

	next() {
		if (
			this.refs.swatchList.scrollLeft + this.refs.swatchList.clientWidth >=
			this.refs.swatchList.scrollWidth
		) {
			this.refs.swatchList.scrollTo({
				left: 0,
				behavior: "smooth",
			});
		} else {
			this.refs.swatchList.scrollBy({
				left: this.refs.swatchList.clientWidth,
				behavior: "smooth",
			});
		}
	}

	handleSwatchClick(event) {
		const { target: link } = event;

		const handle = link.dataset.handle;
		const card = this.closest(".product-card");
		if (card && handle) {
			event.preventDefault();
			productCardLoader.addRequest(handle).then(async (newCard) => {
				startViewTransition(() => {
					card.replaceWith(newCard);
					// morph(card, newCard);
					newCard?.querySelector(`${ProductSwatches.tagName} [data-handle="${handle}"]`)?.focus({
						preventScroll: true,
					});
				}, ["product-card"]);
			});

			globalThis.clarity?.("event", "Product Swatch Click");
			globalThis.klaviyo?.push(["track", "Product Swatch Click", { handle }]);
		}
	}

	handleSwatchHover(event) {
		const { target: link } = event;
		const title = link.dataset.title;
		const handle = link.dataset.handle;
		if (handle && this.closest("product-card")) productCardLoader.addRequest(handle);
		if (title && this.refs.title?.dataset?.originalTitle) {
			clearTimeout(this.#titleRestoreTimeout);
			this.refs.title.textContent = title;
		}

		if (
			handle &&
			this.closest(".product-details") &&
			HTMLScriptElement.supports &&
			HTMLScriptElement.supports("speculationrules") &&
			!document.querySelector(`#speculation-rules-for-${handle}`)
		) {
			const existingSpecScript = document.querySelector(".product-swatches-speculation-rules");
			const specScript = document.createElement("script");
			existingSpecScript?.remove();
			specScript.type = "speculationrules";
			specScript.id = `speculation-rules-for-${handle}`;
			specScript.className = "product-swatches-speculation-rules";
			specScript.textContent = JSON.stringify({
				prerender: [
					{
						urls: [link.href],
					},
				],
			});
			document.body.append(specScript);
		}
	}

	handleSwatchLeave(event) {
		const { target: link } = event;
		const title = link.dataset.title;
		if (title && this.refs.title?.dataset?.originalTitle) {
			this.#titleRestoreTimeout = setTimeout(() => {
				this.refs.title.textContent = this.refs.title.dataset.originalTitle;
			}, 500);
		}
	}

	emit(type, detail = {}) {
		const event = new CustomEvent(`product-swatches:${type}`, {
			detail,
			bubbles: true,
			cancelable: true,
			composed: true,
		});
		this.dispatchEvent(event);
	}
}
